import { format } from 'date-fns';
import { Check, ChevronDown, ChevronRight, ClipboardEdit, Clock, Code, ExternalLink, Headphones, Mail, Phone, PhoneCall, X } from 'lucide-react';
import React from 'react';

import { InspectArtifactButton } from '@/components/artifact-inspector/InspectArtifactButton';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ArtifactType } from '@/services/types/artifact-digest-types';

/** Step trace from AgentFlow execution */
export interface StepTrace {
  step_id: string | number;
  step_type: string;
  workflow_id: string;
  status: 'running' | 'completed' | 'failed' | 'waiting';
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
  error_message?: string;
  wait_until?: string;
  wait_label?: string;
  children?: StepTrace[];
}

export interface ExecutionHistoryProps {
  /** Step traces from workflow execution */
  traces: StepTrace[];
  /** Matter ID for artifact links */
  matterId?: string;
  /** Max number of items to show initially */
  maxItems?: number;
  /** Callback when user wants to make a dialpad call */
  onMakeCall?: (phoneNumber?: string) => void;
  /** Callback when user wants to enter BPO results */
  onEnterResults?: (stepId: string | number) => void;
}

/** Check if step type is a dialpad/BPO call step */
const isDialpadStep = (stepType: string): boolean => {
  const lowerType = stepType.toLowerCase();
  return (
    lowerType.includes('dialpad') ||
    lowerType.includes('bpo') ||
    (lowerType.includes('call') && !lowerType.includes('bland'))
  );
};

const getStepIcon = (stepType: string) => {
  const lowerType = stepType.toLowerCase();
  if (lowerType.includes('email')) {
    return <Mail className="h-3 w-3" />;
  }
  if (lowerType.includes('call') || lowerType.includes('bland') || lowerType.includes('dialpad') || lowerType.includes('bpo')) {
    return <Phone className="h-3 w-3" />;
  }
  if (lowerType.includes('wait')) {
    return <Clock className="h-3 w-3" />;
  }
  return <Clock className="h-3 w-3" />;
};

const getStepTypeLabel = (stepType: string): string => {
  switch (stepType) {
    case 'BlandBlock':
      return 'Voice AI Call';
    case 'DialpadCallBlock':
      return 'RingCentral Call';
    case 'BPOBlock':
      return 'BPO Call';
    case 'SendEmailBlock':
      return 'Email';
    case 'SendTaskNotificationBlock':
      return 'Notification';
    case 'Wait':
      return 'Wait';
    default:
      return stepType.replace(/Block$/, '').replace(/([A-Z])/g, ' $1').trim();
  }
};

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  return `${Math.round(ms / 60000)}m`;
};

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// ============================================================================
// Helper to format phone numbers
// ============================================================================

const formatPhoneNumber = (phone: string | undefined): string => {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

// ============================================================================
// Detail Row - Reusable component for displaying key-value pairs
// ============================================================================

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
  className?: string;
}> = ({ label, value, className }) => (
  <div className={cn('flex items-start gap-2', className)}>
    <span className="text-[10px] font-medium text-zinc-500 min-w-[80px] shrink-0">{label}:</span>
    <span className="text-[11px] text-zinc-800">{value || '—'}</span>
  </div>
);

// ============================================================================
// Evaluation Table - Reusable table for evaluation results
// ============================================================================

const formatEvalValue = (value: unknown): React.ReactNode => {
  if (typeof value === 'boolean') {
    return (
      <span className={cn(
        'px-1.5 py-0.5 rounded text-[10px] font-medium',
        value ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600',
      )}>
        {value ? 'Yes' : 'No'}
      </span>
    );
  }
  if (value === null || value === undefined) {
    return <span className="text-zinc-400">—</span>;
  }
  if (typeof value === 'object') {
    return <span className="font-mono text-[10px]">{JSON.stringify(value)}</span>;
  }
  return String(value);
};

const formatEvalKey = (key: string): string => {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

const EvaluationTable: React.FC<{
  evaluation: Record<string, unknown>;
  title?: string;
  colorScheme?: 'violet' | 'blue';
}> = ({ evaluation, title = 'Evaluation', colorScheme = 'violet' }) => {
  if (!evaluation || Object.keys(evaluation).length === 0) return null;

  const colors = colorScheme === 'violet'
    ? { bg: 'bg-violet-50', border: 'border-violet-100', headerBg: 'bg-violet-100', headerText: 'text-violet-700', cellText: 'text-violet-900' }
    : { bg: 'bg-blue-50', border: 'border-blue-100', headerBg: 'bg-blue-100', headerText: 'text-blue-700', cellText: 'text-blue-900' };

  return (
    <div className="border-t border-zinc-100 pt-2">
      <div className="text-[10px] font-medium text-zinc-500 mb-1.5">{title}</div>
      <div className={cn('rounded border overflow-hidden', colors.border)}>
        <table className="w-full text-[11px]">
          <tbody>
            {Object.entries(evaluation).map(([key, value], index) => (
              <tr
                key={key}
                className={cn(
                  index % 2 === 0 ? colors.bg : 'bg-white',
                )}
              >
                <td className={cn('px-2 py-1.5 font-medium', colors.headerText, 'w-[140px]')}>
                  {formatEvalKey(key)}
                </td>
                <td className={cn('px-2 py-1.5', colors.cellText)}>
                  {formatEvalValue(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// Voice AI Call Details (BlandBlock)
// ============================================================================

// Helper to parse a value that might be a JSON string
const parseIfJsonString = (value: unknown): unknown => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
  return value;
};

// Helper to extract and flatten the evaluation/result from output
// Handles: output.evaluation.result (where result is a JSON string)
const extractEvaluation = (output: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
  if (!output) return undefined;

  // Check for output.evaluation.result (the actual structure from the API)
  const evaluation = output.evaluation as Record<string, unknown> | undefined;
  if (evaluation) {
    const {result} = evaluation;
    if (result) {
      // result is often a JSON string that needs to be parsed
      const parsed = parseIfJsonString(result);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    }
    // If no result key, return evaluation itself
    return evaluation;
  }

  return undefined;
};

const VoiceAICallDetails: React.FC<{
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  startedAt?: string;
  matterId?: string;
}> = ({ input, output, startedAt, matterId }) => {
  const phoneNumber = (input.phone_number || output.phone_number) as string | undefined;
  const audioConversationId = output.audio_conversation_id as string | undefined;
  const callStatus = output.call_status as string | undefined;

  // Extract evaluation from output (handles nested Result wrappers)
  const evaluation = React.useMemo(() => extractEvaluation(output), [output]);

  // Get summary from evaluation or output
  const summary = (evaluation?.summary || evaluation?.call_summary || output.summary) as string | undefined;

  return (
    <div className="space-y-3">
      {/* Call Info */}
      <div className="space-y-1.5">
        <DetailRow label="Phone" value={formatPhoneNumber(phoneNumber)} />
        <DetailRow label="Called" value={startedAt ? formatDate(startedAt) : '—'} />
        <DetailRow
          label="Status"
          value={
            <span className={cn(
              'px-1.5 py-0.5 rounded text-[10px] font-medium',
              callStatus === 'completed' && 'bg-emerald-100 text-emerald-700',
              callStatus === 'failed' && 'bg-red-100 text-red-700',
              callStatus === 'voicemail' && 'bg-amber-100 text-amber-700',
              !['completed', 'failed', 'voicemail'].includes(callStatus || '') && 'bg-zinc-100 text-zinc-700',
            )}>
              {callStatus || 'Unknown'}
            </span>
          }
        />
        {audioConversationId && matterId && (
          <DetailRow
            label="Recording"
            value={
              <InspectArtifactButton
                artifactId={audioConversationId}
                artifactType={ArtifactType.AUDIO_CONVERSATION}
                matterId={matterId}
                compact
                icon={<Headphones className="h-3 w-3" />}
              />
            }
          />
        )}
      </div>

      {/* Summary */}
      {summary && (
        <div className="border-t border-zinc-100 pt-2">
          <div className="text-[10px] font-medium text-zinc-500 mb-1">Call Summary</div>
          <p className="text-[11px] text-zinc-700 leading-relaxed bg-zinc-50 rounded p-2">
            {summary}
          </p>
        </div>
      )}

      {/* Evaluation */}
      <EvaluationTable evaluation={evaluation || {}} colorScheme="violet" />
    </div>
  );
};

// ============================================================================
// Email Details (SendEmailBlock)
// ============================================================================

const EmailDetails: React.FC<{
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  startedAt?: string;
}> = ({ input, output, startedAt }) => {
  const toRecipients = (input.to as string[]) || [];
  const subject = input.subject as string | undefined;
  const body = input.body as string | undefined;
  const conversationUrl = output.conversation_url as string | undefined;

  // Strip HTML tags for preview
  const bodyPreview = body
    ? body.replace(/<[^>]*>/g, '').slice(0, 200) + (body.length > 200 ? '...' : '')
    : undefined;

  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <DetailRow label="To" value={toRecipients.join(', ') || '—'} />
        <DetailRow label="Subject" value={subject} />
        <DetailRow label="Sent" value={startedAt ? formatDate(startedAt) : '—'} />
        {conversationUrl && (
          <DetailRow
            label="View"
            value={
              <a
                href={conversationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-violet-600 hover:text-violet-700 flex items-center gap-1"
              >
                Open in Front <ExternalLink className="h-3 w-3" />
              </a>
            }
          />
        )}
      </div>

      {/* Email Preview */}
      {bodyPreview && (
        <div className="border-t border-zinc-100 pt-2">
          <div className="text-[10px] font-medium text-zinc-500 mb-1">Preview</div>
          <p className="text-[11px] text-zinc-600 leading-relaxed bg-zinc-50 rounded p-2">
            {bodyPreview}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Notification Details (SendTaskNotificationBlock)
// ============================================================================

const NotificationDetails: React.FC<{
  input: Record<string, unknown>;
  startedAt?: string;
}> = ({ input, startedAt }) => {
  const title = input.title as string | undefined;
  const description = input.description as string | undefined;
  const category = input.category as string | undefined;

  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <DetailRow label="Title" value={title} />
        <DetailRow label="Sent" value={startedAt ? formatDate(startedAt) : '—'} />
        {category && (
          <DetailRow
            label="Type"
            value={
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                {category}
              </span>
            }
          />
        )}
      </div>

      {/* Description */}
      {description && (
        <div className="border-t border-zinc-100 pt-2">
          <div className="text-[10px] font-medium text-zinc-500 mb-1">Content</div>
          <p className="text-[11px] text-zinc-700 leading-relaxed bg-zinc-50 rounded p-2">
            {description}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Task Update Details (SendTaskUpdateBlock) - Adds comment/activity to task
// ============================================================================

const TaskCommentDetails: React.FC<{
  input: Record<string, unknown>;
  startedAt?: string;
}> = ({ input, startedAt }) => {
  const update = input.update as string | undefined;
  const updateType = input.update_type as string | undefined;
  const agent = input.agent as string | undefined;

  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <DetailRow label="Time" value={startedAt ? formatDate(startedAt) : '—'} />
        {updateType && (
          <DetailRow
            label="Type"
            value={
              <span className={cn(
                'px-1.5 py-0.5 rounded text-[10px] font-medium',
                updateType === 'ACTIVITY' ? 'bg-violet-100 text-violet-700' : 'bg-zinc-100 text-zinc-700',
              )}>
                {updateType}
              </span>
            }
          />
        )}
        {agent && <DetailRow label="By" value={agent.replace(/_/g, ' ')} />}
      </div>

      {/* Update Content */}
      {update && (
        <div className="border-t border-zinc-100 pt-2">
          <div className="text-[10px] font-medium text-zinc-500 mb-1">Comment</div>
          <p className="text-[11px] text-zinc-700 leading-relaxed bg-amber-50 border border-amber-100 rounded p-2">
            {update}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Task Field Update Details (UpdateTaskBlock) - Updates task fields like due_date
// ============================================================================

const TaskFieldUpdateDetails: React.FC<{
  input: Record<string, unknown>;
  startedAt?: string;
}> = ({ input, startedAt }) => {
  // Extract the fields that were updated (non-null values except task_id and matter_id)
  const updatedFields: Record<string, unknown> = {};
  const skipFields = ['task_id', 'matter_id', 'success'];

  Object.entries(input).forEach(([key, value]) => {
    if (!skipFields.includes(key) && value !== null && value !== undefined) {
      updatedFields[key] = value;
    }
  });

  // Format specific field values for display
  const formatFieldValue = (key: string, value: unknown): React.ReactNode => {
    if (key === 'due_date' || key === 'sla_resolution_target') {
      const dateVal = value as string;
      try {
        return formatDate(dateVal);
      } catch {
        return String(value);
      }
    }
    if (typeof value === 'boolean') {
      return (
        <span className={cn(
          'px-1.5 py-0.5 rounded text-[10px] font-medium',
          value ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600',
        )}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    }
    return String(value);
  };

  return (
    <div className="space-y-2">
      <DetailRow label="Time" value={startedAt ? formatDate(startedAt) : '—'} />

      {/* Updated Fields Table */}
      {Object.keys(updatedFields).length > 0 && (
        <div className="border-t border-zinc-100 pt-2">
          <div className="text-[10px] font-medium text-zinc-500 mb-1.5">Fields Updated</div>
          <div className="rounded border border-amber-100 overflow-hidden">
            <table className="w-full text-[11px]">
              <tbody>
                {Object.entries(updatedFields).map(([key, value], index) => (
                  <tr
                    key={key}
                    className={index % 2 === 0 ? 'bg-amber-50' : 'bg-white'}
                  >
                    <td className="px-2 py-1.5 font-medium text-amber-700 w-[140px]">
                      {formatEvalKey(key)}
                    </td>
                    <td className="px-2 py-1.5 text-amber-900">
                      {formatFieldValue(key, value)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Dialpad/BPO Call Details
// ============================================================================

const DialpadBPOCallDetails: React.FC<{
  input: Record<string, unknown>;
  output: Record<string, unknown>;
  startedAt?: string;
  matterId?: string;
  stepType: string;
}> = ({ input, output, startedAt, matterId, stepType }) => {
  const phoneNumber = (input.phone_number || output.phone_number || input.adjuster_phone) as string | undefined;
  const audioConversationId = output.audio_conversation_id as string | undefined;
  const timedOut = output.timed_out as boolean | undefined;
  const isBPO = stepType.toLowerCase().includes('bpo');

  // Extract evaluation from output (handles nested Result wrappers)
  const evaluation = React.useMemo(() => extractEvaluation(output), [output]);

  return (
    <div className="space-y-3">
      {/* Call Info */}
      <div className="space-y-1.5">
        <DetailRow label="Phone" value={formatPhoneNumber(phoneNumber)} />
        <DetailRow label="Called" value={startedAt ? formatDate(startedAt) : '—'} />
        <DetailRow
          label="Type"
          value={
            <span className={cn(
              'px-1.5 py-0.5 rounded text-[10px] font-medium',
              isBPO ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700',
            )}>
              {isBPO ? 'BPO Call' : 'RingCentral Call'}
            </span>
          }
        />
        {timedOut && (
          <DetailRow
            label="Status"
            value={<span className="text-amber-600 text-[11px]">Timed out waiting for evaluation</span>}
          />
        )}
        {audioConversationId && matterId && (
          <DetailRow
            label="Recording"
            value={
              <InspectArtifactButton
                artifactId={audioConversationId}
                artifactType={ArtifactType.AUDIO_CONVERSATION}
                matterId={matterId}
                compact
                icon={<Headphones className="h-3 w-3" />}
              />
            }
          />
        )}
      </div>

      {/* Evaluation */}
      <EvaluationTable evaluation={evaluation || {}} title="Evaluation Results" colorScheme="blue" />
    </div>
  );
};

// ============================================================================
// Step Details View - Routes to the appropriate custom renderer
// ============================================================================

const StepDetailsView: React.FC<{
  trace: StepTrace;
  matterId?: string;
}> = ({ trace, matterId }) => {
  const [showRawJson, setShowRawJson] = React.useState(false);
  const stepType = trace.step_type.toLowerCase();

  // Merge input and output for the raw JSON view
  const rawData = {
    ...(trace.input && { input: trace.input }),
    ...(trace.output && { output: trace.output }),
  };

  const renderCustomView = () => {
    // Voice AI Call (BlandBlock)
    if (stepType.includes('bland')) {
      return (
        <VoiceAICallDetails
          input={trace.input || {}}
          output={trace.output || {}}
          startedAt={trace.started_at}
          matterId={matterId}
        />
      );
    }

    // Email (SendEmailBlock)
    if (stepType.includes('email')) {
      return (
        <EmailDetails
          input={trace.input || {}}
          output={trace.output || {}}
          startedAt={trace.started_at}
        />
      );
    }

    // Dialpad or BPO Call
    if (stepType.includes('dialpad') || stepType.includes('bpo')) {
      return (
        <DialpadBPOCallDetails
          input={trace.input || {}}
          output={trace.output || {}}
          startedAt={trace.started_at}
          matterId={matterId}
          stepType={trace.step_type}
        />
      );
    }

    // Notification (SendTaskNotificationBlock)
    if (stepType.includes('notification')) {
      return (
        <NotificationDetails
          input={trace.input || {}}
          startedAt={trace.started_at}
        />
      );
    }

    // Task Field Update (UpdateTaskBlock) - updates due_date, name, etc.
    if (stepType === 'updatetaskblock' || stepType.includes('updatetask') || stepType.includes('taskduedate')) {
      return (
        <TaskFieldUpdateDetails
          input={trace.input || {}}
          startedAt={trace.started_at}
        />
      );
    }

    // Task Comment/Activity (SendTaskUpdateBlock) - adds comment to task
    if (stepType.includes('sendtaskupdate')) {
      return (
        <TaskCommentDetails
          input={trace.input || {}}
          startedAt={trace.started_at}
        />
      );
    }

    // Default: show a simple summary with basic info
    return (
      <div className="space-y-1.5">
        <DetailRow label="Type" value={getStepTypeLabel(trace.step_type)} />
        <DetailRow label="Time" value={trace.started_at ? formatDate(trace.started_at) : '—'} />
        {trace.duration_ms && <DetailRow label="Duration" value={formatDuration(trace.duration_ms)} />}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Error display */}
      {trace.error_message && (
        <div className="rounded border border-red-200 bg-red-50 p-2">
          <div className="text-[10px] font-medium text-red-800 mb-1">Error:</div>
          <div className="text-[10px] text-red-700 font-mono">{trace.error_message}</div>
        </div>
      )}

      {/* Custom view or raw JSON */}
      {showRawJson ? (
        <div className="space-y-2">
          <pre className="text-[10px] bg-zinc-900 text-zinc-100 rounded p-3 overflow-x-auto font-mono whitespace-pre-wrap max-h-[300px] overflow-y-auto">
            {JSON.stringify(rawData, null, 2)}
          </pre>
        </div>
      ) : (
        renderCustomView()
      )}

      {/* Toggle button */}
      <div className="flex justify-end pt-1 border-t border-zinc-100">
        <button
          type="button"
          onClick={() => setShowRawJson(!showRawJson)}
          className="text-[10px] text-zinc-500 hover:text-zinc-700 flex items-center gap-1"
        >
          <Code className="h-3 w-3" />
          {showRawJson ? 'Show Formatted' : 'View Raw JSON'}
        </button>
      </div>
    </div>
  );
};

/** Extract summary info from step input/output for display */
const getStepSummary = (
  stepType: string,
  input?: Record<string, unknown>,
  output?: Record<string, unknown>
): { inputSummary?: string; outputSummary?: string } => {
  const result: { inputSummary?: string; outputSummary?: string } = {};

  // Extract input summary based on step type
  if (input) {
    const lowerType = stepType.toLowerCase();
    if (lowerType.includes('bland') || lowerType.includes('voice')) {
      const phone = input.phone_number || input.to_phone;
      if (phone) result.inputSummary = `Called ${phone}`;
    } else if (lowerType.includes('dialpad')) {
      const phone = input.adjuster_phone || input.phone_number;
      if (phone) result.inputSummary = `RingCentral to ${phone}`;
    } else if (lowerType.includes('bpo')) {
      const phone = input.phone_number;
      if (phone) result.inputSummary = `BPO call to ${phone}`;
    } else if (lowerType.includes('email')) {
      const recipient = input.recipient_email || input.to_email;
      if (recipient) result.inputSummary = `Email to ${recipient}`;
    } else if (lowerType.includes('text') || lowerType.includes('sms')) {
      const phone = input.phone_number;
      if (phone) result.inputSummary = `Text to ${phone}`;
    }
  }

  // Extract output summary based on step type
  if (output) {
    const lowerType = stepType.toLowerCase();
    if (lowerType.includes('bland') || lowerType.includes('voice')) {
      // Voice AI call output
      const status = output.call_status || output.status;
      const summary = output.summary || output.call_summary;
      if (output.outcome) {
        result.outputSummary = String(output.outcome);
      } else if (summary) {
        // Truncate long summaries
        const summaryStr = String(summary);
        result.outputSummary = summaryStr.length > 80
          ? `${summaryStr.slice(0, 80)}...`
          : summaryStr;
      } else if (status) {
        result.outputSummary = String(status);
      }
    } else if (lowerType.includes('dialpad')) {
      const outcome = output.outcome || output.call_outcome;
      const notes = output.notes || output.call_notes;
      if (outcome) {
        result.outputSummary = String(outcome);
      } else if (notes) {
        const notesStr = String(notes);
        result.outputSummary = notesStr.length > 80
          ? `${notesStr.slice(0, 80)}...`
          : notesStr;
      }
    } else if (lowerType.includes('bpo')) {
      // BPO evaluation output
      const evaluation = output.evaluation as Record<string, unknown> | undefined;
      const outcome = evaluation?.outcome || output.outcome;
      const acknowledged = evaluation?.acknowledged || output.acknowledged;
      if (outcome) {
        result.outputSummary = String(outcome);
      } else if (acknowledged !== undefined) {
        result.outputSummary = acknowledged ? 'Acknowledged' : 'Not acknowledged';
      }
    } else if (lowerType.includes('email')) {
      const sent = output.sent || output.success;
      const messageId = output.message_id;
      if (sent) {
        result.outputSummary = 'Sent';
      } else if (messageId) {
        result.outputSummary = 'Sent';
      }
    } else if (lowerType.includes('text') || lowerType.includes('sms')) {
      const sent = output.sent || output.success;
      if (sent) {
        result.outputSummary = 'Sent';
      }
    }
  }

  return result;
};

export const ExecutionHistory: React.FC<ExecutionHistoryProps> = ({
  traces,
  matterId,
  maxItems = 10,
  onMakeCall,
  onEnterResults,
}) => {
  const [showAll, setShowAll] = React.useState(false);
  const [expandedStepId, setExpandedStepId] = React.useState<
    string | number | null
  >(null);

  if (!traces || traces.length === 0) {
    return (
      <div className="text-xs text-zinc-500 py-2 text-center">
        No execution history yet
      </div>
    );
  }

  // Filter to show only relevant steps (not Wait steps unless they're the only ones)
  const relevantTraces = traces.filter(
    (t) => t.step_type !== 'Wait' || t.status === 'waiting',
  );

  const displayTraces = showAll
    ? relevantTraces
    : relevantTraces.slice(0, maxItems);
  const hasMore = relevantTraces.length > maxItems;

  const formatTimestamp = (timestamp: string | undefined) => {
    if (!timestamp) return 'N/A';
    try {
      return format(new Date(timestamp), 'MMM d, h:mm:ss a');
    } catch {
      return timestamp;
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-1.5">
        {displayTraces.map((trace, index) => {
          const isSuccess = trace.status === 'completed';
          const isFailed = trace.status === 'failed';
          const isRunning = trace.status === 'running';
          const isWaiting = trace.status === 'waiting';
          const isExpanded = expandedStepId === trace.step_id;

          // Extract audio conversation ID from output if available
          const audioConversationId = trace.output
            ?.audio_conversation_id as string | undefined;

          // Get formatted summary of inputs/outputs
          const { inputSummary, outputSummary } = getStepSummary(
            trace.step_type,
            trace.input,
            trace.output ?? undefined,
          );

          // Check if this step has input/output to show
          const hasDetails =
            (trace.input && Object.keys(trace.input).length > 0) ||
            (trace.output && Object.keys(trace.output).length > 0) ||
            trace.error_message;

          return (
            <div
              key={trace.workflow_id || index}
              className={cn(
                'rounded border overflow-hidden transition-all',
                isSuccess && 'border-emerald-200 bg-emerald-50/50',
                isFailed && 'border-red-200 bg-red-50/50',
                isRunning && 'border-violet-200 bg-violet-50/50',
                isWaiting && 'border-amber-200 bg-amber-50/50',
                !isSuccess &&
                  !isFailed &&
                  !isRunning &&
                  !isWaiting &&
                  'border-zinc-200 bg-white',
              )}
            >
              {/* Clickable Header */}
              <div
                role="button"
                tabIndex={0}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer transition-colors',
                  hasDetails && 'hover:bg-black/5',
                )}
                onClick={() =>
                  hasDetails &&
                  setExpandedStepId(isExpanded ? null : trace.step_id)
                }
                onKeyDown={(e) => {
                  if (hasDetails && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    setExpandedStepId(isExpanded ? null : trace.step_id);
                  }
                }}
              >
                {/* Expand/Collapse indicator */}
                {hasDetails ? (
                  <div className="text-zinc-400 shrink-0">
                    {isExpanded ? (
                      <ChevronDown className="h-3 w-3" />
                    ) : (
                      <ChevronRight className="h-3 w-3" />
                    )}
                  </div>
                ) : (
                  <div className="w-3 shrink-0" />
                )}

                {/* Status icon */}
                <div
                  className={cn(
                    'flex items-center justify-center w-5 h-5 rounded-full shrink-0',
                    isSuccess && 'bg-emerald-500 text-white',
                    isFailed && 'bg-red-500 text-white',
                    isRunning && 'bg-violet-500 text-white',
                    isWaiting && 'bg-amber-500 text-white',
                  )}
                >
                  {isSuccess && <Check className="h-3 w-3" />}
                  {isFailed && <X className="h-3 w-3" />}
                  {isRunning && getStepIcon(trace.step_type)}
                  {isWaiting && <Clock className="h-3 w-3" />}
                </div>

                {/* Step info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-zinc-800 truncate">
                      {trace.wait_label || getStepTypeLabel(trace.step_type)}
                    </span>
                    {trace.duration_ms && (
                      <span className="text-zinc-400 text-[10px]">
                        {formatDuration(trace.duration_ms)}
                      </span>
                    )}
                  </div>
                  {/* Input summary (e.g., phone number, email recipient) */}
                  {inputSummary && !isExpanded && (
                    <div className="text-[10px] text-zinc-500 truncate">
                      {inputSummary}
                    </div>
                  )}
                  {/* Output summary (e.g., call outcome, acknowledgement) */}
                  {outputSummary && !isExpanded && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            'text-[10px] truncate cursor-help',
                            isSuccess && 'text-emerald-700',
                            isFailed && 'text-red-600',
                            isRunning && 'text-violet-600',
                            !isSuccess &&
                              !isFailed &&
                              !isRunning &&
                              'text-zinc-600',
                          )}
                        >
                          {outputSummary}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[400px]">
                        <p className="text-xs whitespace-pre-wrap">
                          {outputSummary}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {trace.completed_at &&
                    !inputSummary &&
                    !outputSummary &&
                    !isExpanded && (
                      <div className="text-[10px] text-zinc-500">
                        {formatDate(trace.completed_at)}
                      </div>
                    )}
                  {isWaiting && trace.wait_until && !isExpanded && (
                    <div className="text-[10px] text-amber-600">
                      Until {formatDate(trace.wait_until)}
                    </div>
                  )}
                  {isFailed && trace.error_message && !isExpanded && (
                    <div className="text-[10px] text-red-600 truncate">
                      {trace.error_message}
                    </div>
                  )}
                </div>

                {/* Artifact link */}
                {audioConversationId && matterId && (
                  <InspectArtifactButton
                    artifactId={audioConversationId}
                    artifactType={ArtifactType.AUDIO_CONVERSATION}
                    matterId={matterId}
                    compact
                    icon={<Headphones className="h-3 w-3" />}
                  />
                )}

                {/* Action buttons for dialpad/BPO steps */}
                {isDialpadStep(trace.step_type) && (isRunning || isWaiting) && (
                  <div className="flex items-center gap-1 shrink-0">
                    {onMakeCall && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[10px] text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          const phone =
                            (trace.input?.adjuster_phone as string) ||
                            (trace.input?.phone_number as string);
                          onMakeCall(phone);
                        }}
                      >
                        <PhoneCall className="h-3 w-3 mr-1" />
                        Call
                      </Button>
                    )}
                    {onEnterResults && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-[10px] text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEnterResults(trace.step_id);
                        }}
                      >
                        <ClipboardEdit className="h-3 w-3 mr-1" />
                        Results
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-3 py-2 border-t border-zinc-200 bg-white/50">
                  {isWaiting ? (
                    // Waiting state - show wait info
                    <div className="space-y-2">
                      {trace.wait_until && (
                        <div className="text-[11px]">
                          <span className="font-medium text-zinc-600">Resumes at: </span>
                          <span className="text-amber-600">{formatTimestamp(trace.wait_until)}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Non-waiting state - show custom details view
                    <StepDetailsView trace={trace} matterId={matterId} />
                  )}
                </div>
              )}
            </div>
          );
        })}

        {hasMore && !showAll && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="w-full text-center text-[11px] text-violet-600 hover:text-violet-700 py-1"
          >
            Show {relevantTraces.length - maxItems} more steps
          </button>
        )}
      </div>
    </TooltipProvider>
  );
};

export default ExecutionHistory;
